const seen = new Set();

export function resetNotificationToasts() {
  seen.clear();
}

function text(value) {
  return value == null ? "" : String(value).trim();
}

export function toToastEvent(item = {}) {
  const meta = item.meta && typeof item.meta === "object" ? item.meta : {};
  return {
    id: text(item.id || item._id),
    userId: text(item.userId),
    companyId: text(item.companyId),
    type: text(item.type),
    title: text(item.title),
    message: text(item.message || item.body),
    vehicleNumber: text(item.vehicleNumber || meta.vehicleNumber),
    confirmationId: text(item.confirmationId || item.requestId || meta.confirmationId),
    action: text(item.action || meta.action),
    agentUserId: text(item.agentUserId || meta.agentUserId),
    agentName: text(item.agentName || meta.agentName),
    agencyName: text(item.agencyName || meta.agencyName || meta.companyName),
    companyCode: text(item.companyCode || meta.companyCode),
    agentCode: text(item.agentCode || meta.agentCode),
    bankId: text(item.bankId || meta.bankId),
    bankName: text(item.bankName || meta.bankName),
    entityCompanyId: text(item.entityCompanyId || meta.companyId),
    userName: text(item.userName || meta.userName || meta.agentName),
    registeredAt: item.registeredAt || meta.registeredAt || item.createdAt || null,
    createdAt: item.createdAt || null,
    source: text(item.source),
  };
}

function aliasKeys(event) {
  const keys = [];
  if (event.id) keys.push(`id:${event.id}`);
  if (
    event.confirmationId &&
    (event.type === "VEHICLE_TRACED" || event.type === "VEHICLE_CONFIRMATION")
  ) {
    keys.push(`vehicle:${event.confirmationId}`);
  } else if (event.confirmationId && event.type) {
    keys.push(`${event.type}:${event.confirmationId}`);
  }
  if (!keys.length && event.title) keys.push(`text:${event.title}:${event.message}`);
  return keys;
}

export function viewerCanSeeNotification(event, viewer = {}) {
  if (!event?.title) return false;
  const viewerUser = text(viewer.userId || viewer.id || viewer._id);
  const viewerCompany = text(viewer.companyId);
  if (event.companyId && viewerCompany && event.companyId !== viewerCompany) return false;
  if (event.source === "fcm") return Boolean(viewerUser);
  if (!event.userId || !viewerUser || event.userId !== viewerUser) return false;
  return true;
}

export function hasSeenNotification(event) {
  return aliasKeys(toToastEvent(event)).some((key) => seen.has(key));
}

export function rememberNotification(event) {
  aliasKeys(toToastEvent(event)).forEach((key) => seen.add(key));
}

export function acceptNotificationToast(event, viewer) {
  const normalized = toToastEvent(event);
  if (!viewerCanSeeNotification(normalized, viewer)) {
    return { show: false, reason: normalized.title ? "forbidden" : "missing" };
  }
  const keys = aliasKeys(normalized);
  if (!keys.length) return { show: false, reason: "missing" };
  if (keys.some((key) => seen.has(key))) return { show: false, reason: "duplicate" };
  keys.forEach((key) => seen.add(key));
  return { show: true, reason: "new", toast: normalized };
}

export function collectNewToastEvents(items, seeded) {
  const events = (Array.isArray(items) ? items : [])
    .map(toToastEvent)
    .filter((event) => event.title);
  if (!seeded) {
    events.forEach(rememberNotification);
    return { fresh: [], seeded: true };
  }
  return {
    fresh: events.filter((event) => !hasSeenNotification(event)),
    seeded: true,
  };
}

export function notificationToastCategory(type = "") {
  const value = String(type || "").toUpperCase();
  if (value === "VEHICLE_TRACED" || value === "VEHICLE_CONFIRMATION" || value === "TRACE_CONFIRMED") {
    return "confirmation";
  }
  if (value.includes("SEIZED") || value === "INVENTORY_CONFIRMED") return "seized";
  if (value === "RECORD_UPLOAD" || value === "INVENTORY_SUBMITTED") return "case";
  if (value.includes("PAYMENT") && !value.includes("EXPIRED") && !value.includes("FAIL")) return "payment";
  if (
    value === "AGENCY_PROFILE_UPDATE" ||
    value === "BANK_REGISTRATION" ||
    value === "BANK_CREATED" ||
    value === "COMPANY_CREATED" ||
    value === "COMPANY_USER_CREATED" ||
    value === "NEW_REPO_AGENT"
  ) {
    return "agency";
  }
  if (
    value.includes("CANCEL") ||
    value.includes("FAIL") ||
    value.includes("REVISION") ||
    value.includes("EXPIRED")
  ) {
    return "warning";
  }
  return "general";
}

export function relativeNotificationTime(value, now = Date.now()) {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const delta = Math.max(0, now - then);
  if (delta < 60 * 1000) return "Just now";
  const minutes = Math.floor(delta / (60 * 1000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day ago`;
}

export function stackNotificationToasts(items, limit = 3) {
  const list = Array.isArray(items) ? items : [];
  const visible = new Set(
    list
      .filter((item) => !item.leaving)
      .slice(-limit)
      .map((item) => item.key)
  );
  return list.filter((item) => item.leaving || visible.has(item.key));
}

export function notificationTarget(item = {}, role = "") {
  const event = toToastEvent(item);
  const isSsdi = role === "SSDI_SUPER_ADMIN";
  if (
    event.type === "COMPANY_CREATED" ||
    event.type === "COMPANY_USER_CREATED" ||
    event.action === "COMPANY_CREATED" ||
    event.action === "COMPANY_USER_CREATED"
  ) {
    const companyId = event.entityCompanyId || event.companyId;
    if (companyId) return `/ssdi/companies/${encodeURIComponent(companyId)}`;
    return "/ssdi/companies";
  }
  if (event.type === "BANK_CREATED" || event.type === "BANK_REGISTRATION" || event.action === "BANK_CREATED") {
    const bankId = event.bankId || event.companyId;
    if (bankId) return `/ssdi/banks/${encodeURIComponent(bankId)}`;
    return "/ssdi/banks";
  }
  if (event.type === "NEW_REPO_AGENT" || event.action === "NEW_REPO_AGENT") {
    if (isSsdi) {
      const companyId = event.entityCompanyId || event.companyId;
      if (companyId) return `/ssdi/companies/${encodeURIComponent(companyId)}`;
      return "/ssdi/companies";
    }
    if (event.agentUserId) return `/users/${encodeURIComponent(event.agentUserId)}`;
    return "/users";
  }
  if (event.type === "VEHICLE_TRACED" || event.type === "VEHICLE_CONFIRMATION") {
    return "/confirmation?status=PENDING";
  }
  if (!event.confirmationId) return "";
  if (event.type === "INVENTORY_SUBMITTED") {
    return `/confirmation/${encodeURIComponent(event.confirmationId)}`;
  }
  if (
    event.type === "TRACE_CONFIRMED" ||
    event.type === "INVENTORY_REVISION_REQUESTED" ||
    event.type === "INVENTORY_CONFIRMED" ||
    event.action === "INVENTORY_UPDATE"
  ) {
    return `/inventory-update?confirmationId=${encodeURIComponent(event.confirmationId)}`;
  }
  return "";
}
